import { findMeaningById, findWordById, meaningsForWord } from "./grouping.ts";
import { VOCABULARY_SCHEMA_VERSION, type MeaningCard, type VocabularySnapshot, type WordEntry } from "./types.ts";

export function incrementRevision(current: number): number {
  return current + 1;
}

export function snapshotReplacingContent(
  live: VocabularySnapshot,
  imported: VocabularySnapshot,
): VocabularySnapshot {
  return {
    ...imported,
    schemaVersion: VOCABULARY_SCHEMA_VERSION,
    dataRevision: live.dataRevision + 1,
  };
}

export function snapshotWithDataRevision(
  snapshot: VocabularySnapshot,
  next: Omit<
    VocabularySnapshot,
    "schemaVersion" | "dataRevision" | "schedulerState" | "reviewHistory" | "historyTruncated"
  > &
    Partial<Pick<VocabularySnapshot, "schedulerState" | "reviewHistory" | "historyTruncated">>,
): VocabularySnapshot {
  return {
    schemaVersion: snapshot.schemaVersion,
    dataRevision: snapshot.dataRevision + 1,
    schedulerState: next.schedulerState ?? snapshot.schedulerState,
    words: next.words,
    meanings: next.meanings,
    relations: next.relations,
    exerciseSets: next.exerciseSets,
    practiceStates: next.practiceStates,
    reviewHistory: next.reviewHistory ?? snapshot.reviewHistory,
    historyTruncated: next.historyTruncated ?? snapshot.historyTruncated,
  };
}

export function removeMeaningFromSnapshot(
  snapshot: VocabularySnapshot,
  meaningId: string,
): VocabularySnapshot {
  const meaning = findMeaningById(snapshot, meaningId);
  if (!meaning) {
    return snapshot;
  }
  const meanings = snapshot.meanings.filter((item) => item.id !== meaningId);
  const words = meanings.some((item) => item.wordId === meaning.wordId)
    ? snapshot.words
    : snapshot.words.filter((word) => word.id !== meaning.wordId);
  const relations = snapshot.relations.filter(
    (relation) => relation.sourceMeaningId !== meaningId && relation.targetMeaningId !== meaningId,
  );
  const exerciseSets = snapshot.exerciseSets
    .filter((set) => set.meaningId !== meaningId)
    .map((set) => invalidateSetIfDependsOn(set, meaningId, meanings));
  const practiceStates = snapshot.practiceStates.filter((state) => state.meaningId !== meaningId);
  const reviewHistory = snapshot.reviewHistory.filter((event) => event.meaningId !== meaningId);
  const schedulerState = {
    ...snapshot.schedulerState,
    lastPracticedMeaningId:
      snapshot.schedulerState.lastPracticedMeaningId === meaningId
        ? null
        : snapshot.schedulerState.lastPracticedMeaningId,
    lastPresentedCardMeaningIds: {
      hide_meaning:
        snapshot.schedulerState.lastPresentedCardMeaningIds.hide_meaning === meaningId
          ? null
          : snapshot.schedulerState.lastPresentedCardMeaningIds.hide_meaning,
      hide_word:
        snapshot.schedulerState.lastPresentedCardMeaningIds.hide_word === meaningId
          ? null
          : snapshot.schedulerState.lastPresentedCardMeaningIds.hide_word,
    },
  };
  return snapshotWithDataRevision(snapshot, {
    words,
    meanings: markNeedingRegeneration(meanings, exerciseSets),
    relations,
    exerciseSets,
    practiceStates,
    reviewHistory,
    schedulerState,
  });
}

export function removeWordFromSnapshot(
  snapshot: VocabularySnapshot,
  wordId: string,
): VocabularySnapshot {
  const ids = meaningsForWord(snapshot, wordId).map((meaning) => meaning.id);
  let next = snapshot;
  for (const id of ids) {
    next = removeMeaningFromSnapshot(next, id);
  }
  return next;
}

function invalidateSetIfDependsOn(
  set: VocabularySnapshot["exerciseSets"][number],
  deletedMeaningId: string,
  remainingMeanings: MeaningCard[],
): VocabularySnapshot["exerciseSets"][number] {
  const depends =
    deletedMeaningId in set.dependencyRevisions ||
    set.exercises.some((exercise) => exercise.referencedMeaningIds.includes(deletedMeaningId));
  if (!depends) {
    return set;
  }
  const remaining = remainingMeanings.find((meaning) => meaning.id === set.meaningId);
  if (!remaining) {
    return set;
  }
  return { ...set, status: "invalidated" };
}

function markNeedingRegeneration(
  meanings: MeaningCard[],
  exerciseSets: VocabularySnapshot["exerciseSets"],
): MeaningCard[] {
  const invalidated = new Set(
    exerciseSets.filter((set) => set.status === "invalidated").map((set) => set.meaningId),
  );
  return meanings.map((meaning) => {
    if (!invalidated.has(meaning.id)) {
      return meaning;
    }
    return { ...meaning, practiceReadiness: "needs_regeneration" };
  });
}

export function moveMeaningToWord(
  snapshot: VocabularySnapshot,
  meaningId: string,
  destination: WordEntry,
  now: string,
): VocabularySnapshot {
  const meaning = findMeaningById(snapshot, meaningId);
  if (!meaning) {
    return snapshot;
  }
  const sourceWordId = meaning.wordId;
  const meanings = snapshot.meanings.map((item) =>
    item.id === meaningId
      ? {
          ...item,
          wordId: destination.id,
          contentRevision: item.contentRevision + 1,
          practiceReadiness: "needs_regeneration" as const,
          updatedAt: now,
        }
      : item,
  );
  const remainingOnSource = meanings.some((item) => item.wordId === sourceWordId);
  const words = remainingOnSource
    ? snapshot.words.map((word) => (word.id === destination.id ? { ...word, updatedAt: now } : word))
    : snapshot.words
        .filter((word) => word.id !== sourceWordId)
        .map((word) => (word.id === destination.id ? { ...word, updatedAt: now } : word));
  const exerciseSets = snapshot.exerciseSets.map((set) =>
    set.meaningId === meaningId || meaningId in set.dependencyRevisions
      ? { ...set, status: "invalidated" as const }
      : set,
  );
  return snapshotWithDataRevision(snapshot, {
    words,
    meanings: markNeedingRegeneration(meanings, exerciseSets),
    relations: snapshot.relations,
    exerciseSets,
    practiceStates: snapshot.practiceStates,
  });
}

export function wordExistsForNormalized(
  snapshot: VocabularySnapshot,
  normalizedText: string,
): WordEntry | undefined {
  return findWordById(
    snapshot,
    snapshot.words.find((word) => word.normalizedText === normalizedText)?.id ?? "",
  );
}
